/* Shop the Look — dynamic hotspots
 * Mobile: card is teleported to document.body and positioned fixed against viewport.
 * Desktop: card stays inside spot and is positioned via CSS transform.
 */
(function () {
  'use strict';

  if (location.search.indexOf('stl_debug=1') > -1) {
    window.STL_DEBUG = true;
    console.log('[STL] Debug mode active');
  }

  const SECTIONS = document.querySelectorAll('[data-stl]');
  if (!SECTIONS.length) return;

  // ---- Safety: clamp hotspot positions on init ----
  document.querySelectorAll('.stl__spot').forEach(spot => {
    const m = spot.style.cssText.match(/--x:\s*([^;]+);.*?--y:\s*([^;]+)/);
    if (!m) return;
    const x = parseFloat(m[1]);
    const y = parseFloat(m[2]);
    if (isNaN(x) || isNaN(y)) {
      spot.style.display = 'none';
      return;
    }
    const cx = Math.max(2, Math.min(98, x));
    const cy = Math.max(2, Math.min(98, y));
    if (cx !== x || cy !== y) {
      spot.style.setProperty('--x', cx + '%');
      spot.style.setProperty('--y', cy + '%');
    }
  });

  // ---- Theme/app injection cleaner ----
  function isOurs(el) {
    if (!el || !el.classList) return false;
    for (const c of el.classList) {
      if (c.startsWith('stl__') || c.startsWith('stl--')) return true;
    }
    return false;
  }
  function cleanCard(card) {
    if (!card) return;
    card.querySelectorAll('*').forEach(el => {
      if (isOurs(el)) return;
      const tag = el.tagName.toLowerCase();
      const parentIsOurs = el.parentElement && isOurs(el.parentElement);
      if (parentIsOurs && (tag === 'svg' || tag === 'img' || tag === 'span' ||
          tag === 'path' || tag === 'line' || tag === 'circle' ||
          tag === 'polyline' || tag === 'rect')) {
        return;
      }
      el.remove();
    });
  }
  function cleanAllCards() {
    document.querySelectorAll('.stl__card').forEach(cleanCard);
  }
  cleanAllCards();
  setTimeout(cleanAllCards, 500);
  setTimeout(cleanAllCards, 1500);
  setTimeout(cleanAllCards, 3000);

  if ('MutationObserver' in window) {
    document.querySelectorAll('.stl__card').forEach(card => {
      new MutationObserver(muts => {
        if (muts.some(m => m.type === 'childList' && m.addedNodes.length)) {
          cleanCard(card);
        }
      }).observe(card, { childList: true, subtree: true });
    });
  }

  // ---- Environment helpers ----
  const isMobile = () => window.matchMedia('(max-width: 749px)').matches;
  const isTouchDevice = () => ('ontouchstart' in window) || (navigator.maxTouchPoints > 0);
  const isAlwaysOn = (section) => isMobile()
    ? section.dataset.alwaysOnMobile === 'true'
    : section.dataset.alwaysOnDesktop === 'true';

  // ---- Teleport portal for mobile ----
  // We move the card to document.body when opening, so no parent overflow:hidden
  // can ever clip it. We use a marker placeholder to know where to put it back.
  function teleportCardToBody(card, spot) {
    if (card.dataset.stlTeleported === 'true') return;
    // Create placeholder so we know where to return the card
    const placeholder = document.createElement('span');
    placeholder.dataset.stlPlaceholder = 'true';
    placeholder.style.display = 'none';
    card.parentNode.insertBefore(placeholder, card);
    card._stlPlaceholder = placeholder;
    card._stlSpot = spot;
    // Move to body
    document.body.appendChild(card);
    card.dataset.stlTeleported = 'true';
    // Add a class to identify teleported cards
    card.classList.add('stl__card--teleported');
  }

  function teleportCardBack(card) {
    if (card.dataset.stlTeleported !== 'true') return;
    const ph = card._stlPlaceholder;
    if (ph && ph.parentNode) {
      ph.parentNode.insertBefore(card, ph);
      ph.remove();
    }
    delete card.dataset.stlTeleported;
    card._stlPlaceholder = null;
    card._stlSpot = null;
    card.classList.remove('stl__card--teleported');
    // Clear inline fixed positioning
    card.style.removeProperty('top');
    card.style.removeProperty('left');
    card.style.removeProperty('width');
  }

  // ---- Position card ----
  function positionCard(spot, opts) {
    opts = opts || {};
    const card = spot.querySelector('.stl__card') ||
                 (spot._stlCard && document.body.contains(spot._stlCard) ? spot._stlCard : null);
    if (!card) return;

    // Reset desktop shift
    card.style.removeProperty('--stl-shift-x');

    // === MOBILE PATH: teleport + fixed positioning ===
    if (isMobile()) {
      // Only teleport when actually opening
      const isOpening = opts.opening === true;
      if (isOpening && card.dataset.stlTeleported !== 'true') {
        teleportCardToBody(card, spot);
        spot._stlCard = card;
      }

      // If teleported, calculate fixed position
      if (card.dataset.stlTeleported === 'true') {
        cleanCard(card); // strip theme injections before measuring

        const vw = window.innerWidth || document.documentElement.clientWidth;
        const vh = window.innerHeight || document.documentElement.clientHeight;
        const margin = 10;

        // Make card visible momentarily to measure
        const prevDisplay = card.style.display;
        const prevVisibility = card.style.visibility;
        const prevOpacity = card.style.opacity;
        card.style.display = 'block';
        card.style.visibility = 'hidden';
        card.style.opacity = '0';
        card.style.position = 'fixed';
        card.style.left = '0px';
        card.style.top = '0px';
        // Set fixed width inline so measurement is predictable
        const desiredWidth = Math.min(260, vw - 24);
        card.style.width = desiredWidth + 'px';

        // Force layout
        void card.offsetWidth;

        const cardRect = card.getBoundingClientRect();
        const cardWidth = cardRect.width || desiredWidth;
        const cardHeight = cardRect.height || 80;

        // Position above the dot
        const spotRect = spot.getBoundingClientRect();
        const dotCenterX = spotRect.left + spotRect.width / 2;
        const dotTop = spotRect.top;
        const dotBottom = spotRect.bottom;

        // Horizontal: center on dot, then clamp into viewport
        let left = dotCenterX - cardWidth / 2;
        if (left < margin) left = margin;
        if (left + cardWidth > vw - margin) left = vw - margin - cardWidth;

        // Vertical: above dot if there's room, else below
        let top = dotTop - 14 - cardHeight;
        if (top < margin) {
          // Not enough room above → put below
          top = dotBottom + 14;
        }
        // Final vertical clamp
        if (top < margin) top = margin;
        if (top + cardHeight > vh - margin) top = vh - margin - cardHeight;

        // Apply
        card.style.left = Math.round(left) + 'px';
        card.style.top = Math.round(top) + 'px';
        card.style.visibility = prevVisibility || '';
        card.style.opacity = prevOpacity || '';
        if (!opts.opening) {
          card.style.display = prevDisplay || '';
        }

        if (window.STL_DEBUG) {
          console.log('[STL MOBILE]', {
            dotX: Math.round(dotCenterX),
            dotTop: Math.round(dotTop),
            cardW: Math.round(cardWidth),
            cardH: Math.round(cardHeight),
            finalLeft: Math.round(left),
            finalTop: Math.round(top),
            finalRight: Math.round(left + cardWidth),
            finalBottom: Math.round(top + cardHeight),
            vw: vw,
            vh: vh
          });
        }
      }
      return;
    }

    // === DESKTOP PATH: classic shift via CSS variable ===
    let pos = spot.dataset.originalPosition || spot.getAttribute('data-position') || 'top';
    if (pos === 'auto') pos = 'top';
    spot.setAttribute('data-position', pos);

    if (pos !== 'top' && pos !== 'bottom') return;

    const spotRect = spot.getBoundingClientRect();
    const dotCenterX = spotRect.left + spotRect.width / 2;
    const vw = window.innerWidth || document.documentElement.clientWidth;
    const margin = 10;

    let cardWidth = 220;
    const wMatch = getComputedStyle(card).width.match(/([\d.]+)px/);
    if (wMatch) cardWidth = parseFloat(wMatch[1]);

    const cardLeft = dotCenterX - cardWidth / 2;
    const cardRight = dotCenterX + cardWidth / 2;
    let shift = 0;
    if (cardLeft < margin) shift = margin - cardLeft;
    else if (cardRight > vw - margin) shift = (vw - margin) - cardRight;

    if (shift !== 0) {
      card.style.setProperty('--stl-shift-x', Math.round(shift) + 'px');
    }
  }

  // ---- Close all open spots ----
  const closeAll = (except) => {
    document.querySelectorAll('.stl__spot[aria-expanded="true"]').forEach(s => {
      if (s !== except) {
        s.setAttribute('aria-expanded', 'false');
        // Teleport card back if needed
        const card = s._stlCard || s.querySelector('.stl__card');
        if (card && card.dataset.stlTeleported === 'true') {
          // Delay return until after fade-out animation
          setTimeout(() => teleportCardBack(card), 250);
        }
      }
    });
  };

  // ---- Toggle a spot ----
  function toggleSpot(spot, section) {
    if (isAlwaysOn(section)) return;

    const isOpen = spot.getAttribute('aria-expanded') === 'true';
    closeAll(spot);

    if (isOpen) {
      spot.setAttribute('aria-expanded', 'false');
      // Teleport back after animation
      const card = spot._stlCard || spot.querySelector('.stl__card');
      if (card && card.dataset.stlTeleported === 'true') {
        setTimeout(() => teleportCardBack(card), 250);
      }
    } else {
      positionCard(spot, { opening: true });
      spot.setAttribute('aria-expanded', 'true');
    }
  }

  // ---- Add to cart ----
  async function addToCart(button) {
    if (button.dataset.state === 'loading') return;

    if (button.dataset.needsOptions === 'true') {
      const meta = button.closest('.stl__card-meta');
      const proxy = meta && meta.querySelector('[data-stl-quick-buy-proxy]');
      if (proxy) {
        const themeBtn = proxy.querySelector('button, a, .quick-buy, [data-quick-buy]');
        if (themeBtn) {
          themeBtn.click();
          return;
        }
      }
      const url = button.dataset.productUrl;
      if (url) window.location.href = url;
      return;
    }

    const variantId = button.dataset.variantId;
    if (!variantId) return;

    button.dataset.state = 'loading';

    try {
      const res = await fetch(window.Shopify && Shopify.routes && Shopify.routes.root
        ? Shopify.routes.root + 'cart/add.js'
        : '/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: JSON.stringify({ items: [{ id: parseInt(variantId, 10), quantity: 1 }] })
      });

      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.description || data.message || 'Could not add');
      }

      button.dataset.state = 'success';
      document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
      document.dispatchEvent(new CustomEvent('cart:updated', { bubbles: true }));
      button.dispatchEvent(new CustomEvent('add-to-cart', { bubbles: true }));

      const drawer = document.getElementById('site-cart-sidebar');
      if (drawer && typeof drawer.show === 'function') {
        setTimeout(() => drawer.show(), 300);
      }

      setTimeout(() => { button.dataset.state = ''; }, 1600);
    } catch (err) {
      console.error('[STL add-to-cart]', err);
      button.dataset.state = 'error';
      setTimeout(() => { button.dataset.state = ''; }, 1800);
    }
  }

  // ---- Wire up each section ----
  SECTIONS.forEach(section => {
    const spots = section.querySelectorAll('.stl__spot');

    spots.forEach(spot => {
      spot.addEventListener('click', e => {
        if (e.target.closest('.stl__card-img, .stl__card-title, .stl__card-add')) return;
        e.preventDefault();
        e.stopPropagation();
        toggleSpot(spot, section);
      });

      spot.addEventListener('keydown', e => {
        if (e.key === 'Enter' || e.key === ' ') {
          if (e.target.closest('.stl__card-img, .stl__card-title, .stl__card-add')) return;
          e.preventDefault();
          toggleSpot(spot, section);
        }
      });

      if (!isTouchDevice()) {
        spot.addEventListener('mouseenter', () => positionCard(spot));
      }
    });

    section.querySelectorAll('[data-stl-add]').forEach(btn => {
      btn.addEventListener('click', e => {
        e.preventDefault();
        e.stopPropagation();
        addToCart(btn);
      });
    });

    // Position when image loads
    const img = section.querySelector('.stl__image');
    if (img) {
      const repos = () => spots.forEach(s => {
        if (!isMobile()) positionCard(s);
      });
      if (img.complete) requestAnimationFrame(repos);
      else img.addEventListener('load', () => requestAnimationFrame(repos), { once: true });
    }
  });

  // Click outside closes
  document.addEventListener('click', e => {
    if (!e.target.closest('.stl__spot') && !e.target.closest('.stl__card--teleported')) {
      closeAll();
    }
  });
  document.addEventListener('keydown', e => {
    if (e.key === 'Escape') closeAll();
  });

  // Resize / orientation
  let rt;
  const recompute = () => {
    clearTimeout(rt);
    rt = setTimeout(() => {
      // Reposition open card if any
      const openSpot = document.querySelector('.stl__spot[aria-expanded="true"]');
      if (openSpot) positionCard(openSpot);
    }, 100);
  };
  window.addEventListener('resize', recompute);
  window.addEventListener('orientationchange', recompute);

  // Scroll reposition for mobile (card is fixed, dot scrolls with page)
  let scrollRt;
  window.addEventListener('scroll', () => {
    if (!isMobile()) return;
    const openSpot = document.querySelector('.stl__spot[aria-expanded="true"]');
    if (!openSpot) return;
    clearTimeout(scrollRt);
    scrollRt = setTimeout(() => positionCard(openSpot), 30);
  }, { passive: true });
})();